import type { TFunction } from 'i18next'

/**
 * Maps raw backend / network errors to human-readable, localised messages.
 * Pass the i18next `t` function from the calling component.
 *
 * Recognises common backend error patterns:
 *   - Zod validation responses
 *   - 401/403/404/409 status codes
 *   - Marina-specific codes: BERTH_OVERLAP, HAS_RELATIONS, NEGATIVE_STOCK, INVALID_STATE
 *   - Network/fetch failures
 *
 * Falls back to `err_generic` for unknown errors. Never exposes raw stack
 * traces or Zod internals to end users.
 */
export function humanizeError(e: unknown, t: TFunction): string {
  if (!e) return t('common.err_generic')

  // Network-level failure (no response) — fetch typically throws TypeError
  if (e instanceof TypeError) return t('common.err_network')

  if (e instanceof Error) {
    const msg = e.message.toLowerCase()

    // Marina-specific codes (backend returns these as `code` in error body
    // and our fetchers stringify into message)
    if (msg.includes('berth_overlap')) return t('common.err_berth_overlap')
    if (msg.includes('has_relations')) return t('common.err_has_relations')
    if (msg.includes('negative_stock')) return t('common.err_negative_stock')
    if (msg.includes('invalid_state')) return t('common.err_invalid_state')

    // Status-based heuristics
    if (msg.includes('unauthorized') || msg.includes('401')) return t('common.err_unauthorized')
    if (msg.includes('forbidden') || msg.includes('403')) return t('common.err_forbidden')
    if (msg.includes('not found') || msg.includes('404')) return t('common.err_not_found')
    if (msg.includes('conflict') || msg.includes('409')) return t('common.err_conflict')

    // Zod validation
    if (msg.includes('validation') || msg.includes('expected') || msg.includes('required'))
      return t('common.err_validation')

    // Short user-friendly message from backend (less than ~80 chars, no stack)
    if (e.message.length > 0 && e.message.length < 80 && !e.message.includes('\n')) {
      return e.message
    }
  }

  return t('common.err_generic')
}
